import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  CropAreaPixels,
  cropToMinimalBlob,
  IMAGE_WEBP_MIME,
  readFileAsDataURL
} from '../utils/assignmentImageCrop';

interface AssignmentSubmissionCropModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (blob: Blob, mimeType: string) => void;
}

type CropRect = { x: number; y: number; width: number; height: number };
type DragMode = 'move' | 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

const MIN_CROP_SIZE = 48;
/** 조절점이 잘리지 않도록 이미지 바깥 여백 */
const CROP_HANDLE_PAD = 12;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const clampCropRect = (rect: CropRect, boundsW: number, boundsH: number): CropRect => {
  const width = clamp(rect.width, MIN_CROP_SIZE, boundsW);
  const height = clamp(rect.height, MIN_CROP_SIZE, boundsH);
  const x = clamp(rect.x, 0, boundsW - width);
  const y = clamp(rect.y, 0, boundsH - height);
  return { x, y, width, height };
};

const computeDisplaySize = (naturalW: number, naturalH: number, maxW: number, maxH: number) => {
  const scale = Math.min(maxW / naturalW, maxH / naturalH, 1);
  return {
    width: Math.max(1, Math.round(naturalW * scale)),
    height: Math.max(1, Math.round(naturalH * scale)),
    scale
  };
};

/** 이미지 프레임 안 크롭 밖 영역 어둡기 */
const CROP_OUTSIDE_DIM = 0.55;

const toCropPixels = (
  rect: CropRect,
  displayW: number,
  displayH: number,
  naturalW: number,
  naturalH: number
): CropAreaPixels => {
  const ratioX = naturalW / displayW;
  const ratioY = naturalH / displayH;
  return {
    x: Math.round(rect.x * ratioX),
    y: Math.round(rect.y * ratioY),
    width: Math.max(1, Math.round(rect.width * ratioX)),
    height: Math.max(1, Math.round(rect.height * ratioY))
  };
};

const AssignmentSubmissionCropModal: React.FC<AssignmentSubmissionCropModalProps> = ({
  isOpen,
  onClose,
  onSave
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{
    mode: DragMode;
    startX: number;
    startY: number;
    startRect: CropRect;
  } | null>(null);

  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState('');
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });
  const [cropRect, setCropRect] = useState<CropRect>({ x: 0, y: 0, width: 0, height: 0 });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setIsDraggingFile(false);
      setRawImageSrc('');
      setNaturalSize({ width: 0, height: 0 });
      setDisplaySize({ width: 0, height: 0 });
      setCropRect({ x: 0, y: 0, width: 0, height: 0 });
      setIsSaving(false);
      setError('');
      dragRef.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const handleFile = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('이미지 파일만 업로드할 수 있습니다.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('이미지 파일은 10MB 이하만 업로드할 수 있습니다.');
      return;
    }
    setError('');
    const dataUrl = await readFileAsDataURL(file);
    setRawImageSrc(dataUrl);
    setNaturalSize({ width: 0, height: 0 });
    setDisplaySize({ width: 0, height: 0 });
    setCropRect({ x: 0, y: 0, width: 0, height: 0 });
  }, []);

  const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    if (!nw || !nh) return;

    const maxW = Math.min(window.innerWidth - 48, 720);
    const maxH = Math.max(200, window.innerHeight - 240);
    const { width: dw, height: dh } = computeDisplaySize(nw, nh, maxW, maxH);

    setNaturalSize({ width: nw, height: nh });
    setDisplaySize({ width: dw, height: dh });

    setCropRect(clampCropRect({ x: 0, y: 0, width: dw, height: dh }, dw, dh));
  }, []);

  const applyDrag = useCallback(
    (clientX: number, clientY: number) => {
      const drag = dragRef.current;
      if (!drag || !displaySize.width || !displaySize.height) return;

      const dx = clientX - drag.startX;
      const dy = clientY - drag.startY;
      const { startRect, mode } = drag;
      const maxW = displaySize.width;
      const maxH = displaySize.height;

      let next: CropRect = { ...startRect };

      if (mode === 'move') {
        next = {
          ...startRect,
          x: startRect.x + dx,
          y: startRect.y + dy
        };
      } else {
        if (mode.includes('w')) {
          const newX = startRect.x + dx;
          const newWidth = startRect.width - dx;
          next.x = newX;
          next.width = newWidth;
        }
        if (mode.includes('e')) {
          next.width = startRect.width + dx;
        }
        if (mode.includes('n')) {
          const newY = startRect.y + dy;
          const newHeight = startRect.height - dy;
          next.y = newY;
          next.height = newHeight;
        }
        if (mode.includes('s')) {
          next.height = startRect.height + dy;
        }
      }

      setCropRect(clampCropRect(next, maxW, maxH));
    },
    [displaySize.height, displaySize.width]
  );

  useEffect(() => {
    const onPointerMove = (e: PointerEvent) => {
      if (!dragRef.current) return;
      e.preventDefault();
      applyDrag(e.clientX, e.clientY);
    };
    const onPointerUp = () => {
      dragRef.current = null;
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
  }, [applyDrag]);

  const startDrag = (mode: DragMode, clientX: number, clientY: number) => {
    dragRef.current = {
      mode,
      startX: clientX,
      startY: clientY,
      startRect: cropRect
    };
  };

  const handleDrop: React.DragEventHandler<HTMLDivElement> = async (e) => {
    e.preventDefault();
    setIsDraggingFile(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    await handleFile(file);
  };

  const handleSaveClick = async () => {
    if (!rawImageSrc || !naturalSize.width || !displaySize.width || isSaving) return;
    if (cropRect.width < MIN_CROP_SIZE || cropRect.height < MIN_CROP_SIZE) {
      setError('크롭 영역이 너무 작습니다.');
      return;
    }

    const cropPixels = toCropPixels(
      cropRect,
      displaySize.width,
      displaySize.height,
      naturalSize.width,
      naturalSize.height
    );

    setIsSaving(true);
    setError('');
    try {
      let blob = await cropToMinimalBlob(rawImageSrc, cropPixels, 1280);
      if (blob.size > 380_000) {
        blob = await cropToMinimalBlob(rawImageSrc, cropPixels, 960);
      }
      onSave(blob, IMAGE_WEBP_MIME);
      onClose();
    } catch (e) {
      console.error(e);
      setError('이미지 저장에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const isCropMode = !!rawImageSrc && displaySize.width > 0;

  return (
    <Overlay>
      <Modal onClick={(e) => e.stopPropagation()} $hasImage={isCropMode}>
        <Header>
          <Title>과제 사진</Title>
          <CloseButton type="button" onClick={onClose} aria-label="닫기">
            ×
          </CloseButton>
        </Header>

        {!rawImageSrc ? (
          <DropZone
            $isDragging={isDraggingFile}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDraggingFile(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setIsDraggingFile(false);
            }}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            사진을 찍거나 앨범에서 선택하세요
          </DropZone>
        ) : (
          <>
            <CropHint>크롭 영역을 드래그하거나 모서리·변을 당겨 조절하세요.</CropHint>
            <CropOuter
              ref={stageRef}
              style={
                displaySize.width > 0
                  ? {
                      width: displaySize.width + CROP_HANDLE_PAD * 2,
                      height: displaySize.height + CROP_HANDLE_PAD * 2
                    }
                  : undefined
              }
            >
              <CropStageClip
                style={{
                  width: displaySize.width || undefined,
                  height: displaySize.height || undefined,
                  left: CROP_HANDLE_PAD,
                  top: CROP_HANDLE_PAD
                }}
              >
                <StageImage
                  src={rawImageSrc}
                  alt="원본"
                  width={displaySize.width || undefined}
                  height={displaySize.height || undefined}
                  draggable={false}
                  onLoad={handleImageLoad}
                />
                {displaySize.width > 0 && (
                  <DimMaskContainer>
                    <DimPanel
                      style={{
                        left: 0,
                        top: 0,
                        right: 0,
                        height: cropRect.y
                      }}
                    />
                    <DimPanel
                      style={{
                        left: 0,
                        top: cropRect.y + cropRect.height,
                        right: 0,
                        bottom: 0
                      }}
                    />
                    <DimPanel
                      style={{
                        left: 0,
                        top: cropRect.y,
                        width: cropRect.x,
                        height: cropRect.height
                      }}
                    />
                    <DimPanel
                      style={{
                        left: cropRect.x + cropRect.width,
                        top: cropRect.y,
                        right: 0,
                        height: cropRect.height
                      }}
                    />
                  </DimMaskContainer>
                )}
              </CropStageClip>
              {displaySize.width > 0 && (
                <CropOverlay
                  style={{
                    width: displaySize.width,
                    height: displaySize.height,
                    left: CROP_HANDLE_PAD,
                    top: CROP_HANDLE_PAD
                  }}
                >
                  <CropSelection
                    style={{
                      left: cropRect.x,
                      top: cropRect.y,
                      width: cropRect.width,
                      height: cropRect.height
                    }}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
                      startDrag('move', e.clientX, e.clientY);
                    }}
                  >
                    {(['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'] as DragMode[]).map((handle) => (
                      <CropHandle
                        key={handle}
                        data-handle={handle}
                        onPointerDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          startDrag(handle, e.clientX, e.clientY);
                        }}
                      />
                    ))}
                  </CropSelection>
                </CropOverlay>
              )}
            </CropOuter>
          </>
        )}

        {error && <ErrorText>{error}</ErrorText>}

        <Footer>
          <Button type="button" data-variant="modal-secondary" onClick={onClose} disabled={isSaving}>
            취소
          </Button>
          {rawImageSrc && displaySize.width > 0 && (
            <Button type="button" $primary onClick={() => void handleSaveClick()} disabled={isSaving}>
              {isSaving ? '처리 중...' : '사용'}
            </Button>
          )}
        </Footer>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={(e) => {
            const input = e.currentTarget;
            const file = input.files?.[0];
            input.value = '';
            if (file) void handleFile(file);
          }}
        />
      </Modal>
    </Overlay>
  );
};

export default AssignmentSubmissionCropModal;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 30030;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const Modal = styled.div<{ $hasImage?: boolean }>`
  position: relative;
  z-index: 1;
  isolation: isolate;
  width: ${(p) => (p.$hasImage ? 'fit-content' : '100%')};
  max-width: min(calc(100vw - 32px), 760px);
  border-radius: 14px;
  background: var(--color-surface);
  border: var(--modal-border);
  box-shadow: var(--modal-shadow-large);
  padding: 16px;
  overflow: visible;

  :root[data-theme='light'] & {
    background: #ffffff;
  }
`;

const Header = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  background: inherit;
`;

const Title = styled.h3`
  margin: 0;
  color: var(--color-text-primary);
  font-size: 18px;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  font-size: 24px;
  cursor: pointer;
`;

const DropZone = styled.div<{ $isDragging: boolean }>`
  min-height: 220px;
  border: 2px dashed
    ${(p) => (p.$isDragging ? 'var(--color-accent-strong)' : 'var(--color-border)')};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-muted);
  background: var(--color-panel);
  cursor: pointer;
  text-align: center;
  padding: 16px;
`;

const CropHint = styled.p`
  position: relative;
  z-index: 2;
  margin: 0 0 10px 0;
  font-size: 13px;
  color: var(--color-text-muted);
  text-align: center;
  line-height: 1.45;
  background: inherit;
`;

const CropOuter = styled.div`
  position: relative;
  z-index: 1;
  margin: 0 auto;
  max-width: 100%;
  user-select: none;
  touch-action: none;
  overflow: visible;
  isolation: isolate;
`;

const CropStageClip = styled.div`
  position: absolute;
  border-radius: 8px;
  overflow: hidden;
  background: #111;
  contain: paint;
`;

const CropOverlay = styled.div`
  position: absolute;
  overflow: visible;
  z-index: 2;
  pointer-events: none;
`;

const DimMaskContainer = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 1;
`;

const DimPanel = styled.div`
  position: absolute;
  background: rgba(0, 0, 0, ${CROP_OUTSIDE_DIM});
  pointer-events: none;
`;

const StageImage = styled.img`
  position: relative;
  z-index: 0;
  display: block;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const CropSelection = styled.div`
  position: absolute;
  box-sizing: border-box;
  border: 2px solid rgba(255, 255, 255, 0.92);
  cursor: move;
  z-index: 2;
  pointer-events: auto;
  overflow: visible;
  background: transparent;
  box-shadow: none;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border: 1px dashed rgba(255, 255, 255, 0.5);
    pointer-events: none;
  }
`;

const CropHandle = styled.div`
  position: absolute;
  width: 16px;
  height: 16px;
  background: #fff;
  border: 2px solid var(--color-accent-strong);
  border-radius: 3px;
  box-sizing: border-box;
  z-index: 100;
  pointer-events: auto;

  &[data-handle='nw'] {
    left: -8px;
    top: -8px;
    cursor: nwse-resize;
  }
  &[data-handle='n'] {
    left: 50%;
    top: -8px;
    transform: translateX(-50%);
    cursor: ns-resize;
  }
  &[data-handle='ne'] {
    right: -8px;
    top: -8px;
    cursor: nesw-resize;
  }
  &[data-handle='e'] {
    right: -8px;
    top: 50%;
    transform: translateY(-50%);
    cursor: ew-resize;
  }
  &[data-handle='se'] {
    right: -8px;
    bottom: -8px;
    cursor: nwse-resize;
  }
  &[data-handle='s'] {
    left: 50%;
    bottom: -8px;
    transform: translateX(-50%);
    cursor: ns-resize;
  }
  &[data-handle='sw'] {
    left: -8px;
    bottom: -8px;
    cursor: nesw-resize;
  }
  &[data-handle='w'] {
    left: -8px;
    top: 50%;
    transform: translateY(-50%);
    cursor: ew-resize;
  }
`;

const Footer = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  gap: 12px;
  margin-top: 14px;
  width: 100%;
  box-sizing: border-box;
  background: inherit;
`;

const Button = styled.button<{ $primary?: boolean }>`
  flex: 1;
  min-width: 0;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  border: ${(p) => (p.$primary ? 'none' : '1px solid var(--color-border)')};
  background: ${(p) =>
    p.$primary ? 'var(--color-accent-strong)' : 'var(--color-surface)'};
  color: ${(p) => (p.$primary ? '#fff' : 'var(--color-text-primary)')};
  cursor: pointer;
  box-sizing: border-box;

  :root[data-theme='light'] &:not(:disabled) {
    background: ${(p) => (p.$primary ? 'var(--color-accent-strong)' : '#ffffff')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.div`
  position: relative;
  z-index: 2;
  margin-top: 10px;
  color: var(--color-accent-strong);
  font-size: 13px;
  background: inherit;
`;
